import {Inject, Injectable} from '@angular/core';
import {Observable, defer} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';
import {TimekeepingHttpService} from './timekeeping-http.service';
import {CommonsFacadeService} from '../../../commons/angular/state/commons/commons-facade.service';
import type {BringozToastService} from '../../../commons/services/bringoz-toast.service';
import {TimekeepingExportTimecardsPayloadModel} from '../../models/timekeeping-export-timecards-payload.model';
import {TimekeepingExportMileagePayloadModel} from '../../models/timekeeping-export-mileage-payload.model';
import {TimekeepingExportMondelezSummaryPayloadModel} from '../../models/timekeeping-export-mondelez-summary-payload.model';

/**
 * Timekeeping exports are mailed to the user: each call shows the global loader and an "export to mail" toast.
 */
@Injectable({
  providedIn: 'root'
})
export class TimekeepingExportService {

  constructor(@Inject(TimekeepingHttpService) private timekeepingHttpService: TimekeepingHttpService,
              @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService,
              @Inject('bringozToastService') private bringozToastService: BringozToastService) {
  }

  exportTimeCards(payload: TimekeepingExportTimecardsPayloadModel): Observable<void> {
    return this.withFeedback(this.timekeepingHttpService.exportUnapprovedTimeCards(payload), 'Failed to export timecards');
  }

  exportMileage(payload: TimekeepingExportMileagePayloadModel): Observable<void> {
    return this.withFeedback(this.timekeepingHttpService.exportMileage(payload), 'Failed to export mileage');
  }

  exportMondelezSummary(payload: TimekeepingExportMondelezSummaryPayloadModel): Observable<void> {
    return this.withFeedback(this.timekeepingHttpService.exportMondelezSummary(payload), 'Failed to export Mondelez summary report');
  }

  private withFeedback(request$: Observable<void>, errorLog: string): Observable<void> {
    return defer(() => {
      this.commonsFacadeService.setLoaderOn();
      return request$;
    }).pipe(
      tap({
        next: () => this.bringozToastService.success({body: 'COMMONS_EXPORT_TO_MAIL_SUCCESS'}),
        error: (error) => {
          console.error(errorLog, error);
          this.bringozToastService.error({body: 'COMMONS_EXPORT_TO_MAIL_ERROR', details: 'SOMETHING_WENT_WRONG'});
        }
      }),
      finalize(() => this.commonsFacadeService.setLoaderOff())
    );
  }
}
